// Resolved theme for the pages that opt into dark mode.
//
// The resolved value lives on <html data-theme>, not in a media query, because
// there is a manual toggle. A media query cannot be overridden by a button,
// and the two would disagree the moment someone used it.
//
// The attribute is set by an inline script in public/index.html before React
// mounts, so the first paint is already the right theme. Without that the page
// renders light for a frame and then flips, which is worse than no toggle.
//
// Three stored states, not two. "system" is the default and follows the OS as
// it changes; "light" and "dark" are explicit choices that stop following it.
// Storing only a boolean would make "the same as my phone" unexpressible once
// the button had been pressed.

export const STORAGE_KEY = "lineup:theme";

const listeners = new Set();

export function storedPreference() {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : "system";
  } catch {
    // Private mode, or storage blocked. Following the OS is the right
    // fallback, and it is also the default.
    return "system";
  }
}

export function systemTheme() {
  return typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function resolvedTheme() {
  const preference = storedPreference();
  return preference === "system" ? systemTheme() : preference;
}

function apply(theme) {
  document.documentElement.setAttribute("data-theme", theme);
  listeners.forEach((fn) => fn(theme));
}

export function setPreference(preference) {
  try {
    if (preference === "system") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Not being able to remember the choice should not stop it taking effect
    // for this session.
  }
  apply(resolvedTheme());
}

export function subscribe(fn) {
  listeners.add(fn);
  // Only while following the OS: once someone has chosen, an OS change must
  // not override them.
  const query = window.matchMedia?.("(prefers-color-scheme: dark)");
  const onSystemChange = () => {
    if (storedPreference() === "system") apply(systemTheme());
  };
  query?.addEventListener?.("change", onSystemChange);
  return () => {
    listeners.delete(fn);
    query?.removeEventListener?.("change", onSystemChange);
  };
}
